import React, { memo, useEffect, useRef, useState } from 'react'
//components
import EquipmentForm from 'components/common/Forms/GeneralForm/EquipmentForm';
import ConfirmationBox from 'components/common/Message/ConfirmationBox/ConfirmationBox';
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { useDispatch } from 'react-redux';
import { ProgressSpinner } from 'primereact/progressspinner';
import { setIsDevReadingChanged, setIsTestedDateChanged } from 'redux/pages/Inspection/InspectionTask/InspectionDetails/Equipment/EquipmentSlice';

interface EditEquipmentPropType {
  showModal: boolean;
  datafields?: any;
  gridData?: any;
  selectedRow?: any;
  setShowModal?: (state: boolean) => void;
  onHideModal?: () => void;
  onFormSubmit?: (body) => void;
  onClickNext?: () => void;
  onClickPrev?: () => void;
  uniqueEquipmentClassId?: any;
  isFromDrawing?: boolean;
  showProgressBar?: boolean;
  uniqueInspectionTaskId?: any;
  equipmentId?: any;
  attachmentsRefreshKey?: any;
  formRef?: React.MutableRefObject<any>;
}

const EditEquipment: React.FC<EditEquipmentPropType> = (props) => {

  const { showModal, datafields, gridData, selectedRow, onHideModal = () => { }, setShowModal = () => { }, onFormSubmit = () => { }, onClickNext = () => { }, onClickPrev = () => { }, uniqueEquipmentClassId, showProgressBar, isFromDrawing = false, uniqueInspectionTaskId, equipmentId, attachmentsRefreshKey, formRef } = props;
  const localForm = useRef<any>(null);
  const editForm = formRef ?? localForm;
  const dispatch = useDispatch<any>();
  const isSubmitPendingRef = useRef(false);
  // The navigation/close the user asked for, held while they answer the unsaved-images prompt.
  const [pendingAction, setPendingAction] = useState<"NEXT" | "PREV" | "CLOSE" | null>(null);
  // True while any upload zone is still uploading or loading its saved images. The footer buttons
  // stay disabled until then, so the form cannot be saved or navigated away from mid-load.
  const [isImagesBusy, setIsImagesBusy] = useState(false);
  let body = {
    uniqueEquipmentClassId: uniqueEquipmentClassId
  }

  useEffect(() => {
    if (!showProgressBar) {
      isSubmitPendingRef.current = false;
    }
  }, [showProgressBar]);

  // Validation rejected the submit, so nothing was sent and no progress bar will appear to clear
  // the guard below. Release it here or the modal accepts no further Save clicks.
  const onSubmitInvalid = () => {
    isSubmitPendingRef.current = false;
  }

  const formSubmitHandler = () => {
    if (isSubmitPendingRef.current) return;
    isSubmitPendingRef.current = true;
    if (editForm?.current?.isUploadingImages?.()) {
      const waitForUpload = setInterval(() => {
        if (!editForm?.current?.isUploadingImages?.()) {
          clearInterval(waitForUpload);
          editForm?.current?.requestSubmit();
        }
      }, 200);
      return;
    }
    editForm?.current?.requestSubmit();
  }

  const getDisabled = (type) => {
    const currentIndex = gridData?.findIndex((item) => item?.EquipmentId === selectedRow?.[0]?.EquipmentId);

    if (type === "PREV") {
      if (currentIndex > 0) return false
      else return true
    } else {
      if (currentIndex < (gridData?.length - 1)) return false
      else return true
    }
  }
  const onCancelClick =() =>{
    onHideModal();
    dispatch(setIsDevReadingChanged(''));
    dispatch(setIsTestedDateChanged(''));
  }

  const runAction = (action: "NEXT" | "PREV" | "CLOSE") => {
    if (action === "NEXT") onClickNext();
    else if (action === "PREV") onClickPrev();
    else onCancelClick();
  }

  // Images picked in any category live only in this form until Save Changes, so navigating to
  // another equipment or closing discards them. Ask first instead of losing them silently.
  const requestAction = (action: "NEXT" | "PREV" | "CLOSE") => {
    if (editForm?.current?.hasUnsavedFieldImages?.()) {
      setPendingAction(action);
      return;
    }
    runAction(action);
  }

  const onConfirmDiscard = () => {
    const action = pendingAction;
    setPendingAction(null);
    // The prompt says the images will be lost, so actually drop them before moving on. Otherwise
    // they stayed in their zones and showed up against the next equipment.
    editForm?.current?.discardUnsavedFieldImages?.();
    if (action) runAction(action);
  }

  const renderFooter = () => {
    return (
      <>
        <Button label="Cancel" onClick={() => requestAction("CLOSE")} disabled={isImagesBusy} className="pre btn" />

        {!isFromDrawing && (<Button label="Previous" onClick={() => requestAction("PREV")} disabled={isImagesBusy || getDisabled("PREV")} className="pre btn"/>)}

        <Button label="Save Changes " className="update btn" autoFocus disabled={isImagesBusy} onClick={formSubmitHandler} />
        {!isFromDrawing && (<Button label="Next" onClick={() => requestAction("NEXT")} disabled={isImagesBusy || getDisabled("NEXT")} className="pre btn"/>)}
      </>
    );
  };

  return (
    <Dialog header="Edit Equipment" visible={showModal} style={{ width: "40vw", height: "95%" }} onHide={() => requestAction("CLOSE")}
      footer={renderFooter()} className="form-element-modal">

      {showProgressBar && <div className='formElement-modal-spinner'> <ProgressSpinner /> </div> }

      <EquipmentForm ref={editForm} datafields={datafields} isEditForm={true} ddAppendTo='self' onFormSubmit={onFormSubmit} onSubmitInvalid={onSubmitInvalid} body={body} uniqueInspectionTaskId={uniqueInspectionTaskId} equipmentId={equipmentId} attachmentsRefreshKey={attachmentsRefreshKey} onImagesBusyChange={setIsImagesBusy}/>

      <ConfirmationBox
        showConfirmModal={pendingAction !== null}
        setShowConfirmModal={() => setPendingAction(null)}
        confirmAction={onConfirmDiscard}
        cancelAction={() => setPendingAction(null)}
        onHideAction={() => setPendingAction(null)}
        message="The attached image(s) have not been saved and will be lost. Do you want to continue?"
      />

    </Dialog>
  )

}

export default memo(EditEquipment)